// ═══════════════════════════════════════════════════════════════════════════
// PAGE MATRIX — helpers purs (géométrie, couleur, fragments)
// Extrait de page-matrix.ts (découpe god-function `renderMatrixPage` 2026-07) :
// helpers PURS (aucun état de closure) sortis de la fonction-monstre. Zonage de
// sévérité, jitter déterministe, styles d'onglets, tooltip de bulle. Comportement
// identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t, sevColor.

import type { MatrixRisk } from './page-matrix.js';

/** Zone de criticité d'un score de sévérité (proba × impact). */
export function zone(s: number): 'low' | 'med' | 'high' | 'crit' {
  return s >= 9 ? 'crit' : s >= 5 ? 'high' : s >= 3 ? 'med' : 'low';
}

/** Sévérité de coloration d'une cellule (arrondi biaisé, cf. matrice admin). */
export function coloringSeverity(s: number): number {
  return Math.round(s + 0.45);
}

/** Déplacement pseudo-aléatoire DÉTERMINISTE d'une bulle (évite le chevauchement
 * de risques au même point, stable d'un rendu à l'autre car dérivé de l'id). */
export function jitter(id: number, axis: 'x' | 'y'): number {
  const h = ((id * (axis === 'x' ? 2654435761 : 2246822519)) >>> 0) % 1000;
  return (h / 1000 - 0.5) * 0.3;
}

/** Couleur représentative de chaque zone (via `sevColor` du thème admin). */
export function buildZoneColorMap(): Record<string, string> {
  const rep: Record<'low' | 'med' | 'high' | 'crit', number> = {
    low: 1,
    med: 3,
    high: 6,
    crit: 9,
  };
  return Object.fromEntries(Object.entries(rep).map(([z, sev]) => [z, sevColor(sev)]));
}

/** Tronque `s` à `n` caractères avec ellipse. */
export const cut = (s: string, n: number): string => (s.length > n ? s.slice(0, n - 1) + '…' : s);

/** Style inline d'un onglet de révision (actif / inactif). */
export const tabStyle = (active: boolean): string =>
  active
    ? 'padding:9px 16px;cursor:pointer;font-size:13px;font-weight:700;background:var(--bg);color:var(--accent);border:1px solid var(--border);border-bottom:1px solid var(--bg);border-top:3px solid var(--accent);border-radius:6px 6px 0 0;margin-bottom:-1px;position:relative;z-index:2;white-space:nowrap'
    : 'padding:9px 16px;cursor:pointer;font-size:13px;font-weight:500;background:var(--bg2);color:var(--text2);border:1px solid transparent;border-bottom:1px solid var(--border);border-radius:6px 6px 0 0;margin-bottom:-1px;white-space:nowrap';

/** Style inline d'un bouton de navigation de révision (activé / grisé). */
export const navBtnStyle = (enabled: boolean): string =>
  'width:32px;height:32px;flex-shrink:0;display:flex;align-items:center;justify-content:center;' +
  'border:1px solid var(--border2);border-radius:var(--radius);background:var(--bg2);color:var(--text2);' +
  (enabled ? 'cursor:pointer' : 'opacity:.35;cursor:default');

/** Tooltip HTML d'une bulle de risque : nom, conséquence, catégorie/code, score
 * courant coloré par zone, et score précédent si disponible. */
export function matrixTooltipHtml(r: MatrixRisk, zoneColor: Record<string, string>): string {
  const curr = r.probabilite_actuelle * r.impact_actuel;
  const hasPrev = r.probabilite_precedente != null && r.impact_precedent != null;
  const prev = hasPrev ? (r.probabilite_precedente ?? 0) * (r.impact_precedent ?? 0) : null;
  return `<div style="font-size:13px;line-height:1.6;max-width:240px">
      <div style="font-weight:500;margin-bottom:2px;color:#111">${esc(r.nom)}</div>
      ${r.consequence ? `<div style="font-size:11px;color:#6b7280;margin-bottom:5px;font-style:italic">${esc(r.consequence)}</div>` : ''}
      <div style="font-size:11px;color:var(--text3);margin-bottom:4px">${esc(r.categorie)} · ${esc(r.code)}</div>
      <div>${esc(t('matrix.tooltip.proba'))} <strong>${r.probabilite_actuelle}</strong> × ${esc(t('matrix.tooltip.impact'))} <strong>${r.impact_actuel}</strong> = <strong style="color:${zoneColor[zone(curr)]}">${curr}</strong></div>
      ${hasPrev ? `<div style="color:var(--text3);font-size:11px;margin-top:2px">${esc(t('matrix.tooltip.previous'))} ${r.probabilite_precedente} × ${r.impact_precedent} = ${prev}</div>` : ''}
    </div>`;
}
